import {
  AfterViewInit,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
} from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  BaseListItems,
  CardTableComponent,
  ConfirmDialogService,
  CustomPaginator,
  FilterParams,
  LoaderIndicatorDirective,
  SearchComponent,
} from '@t-llevo/common-ui';
import { VersionControlDataService } from '../../services/version-control-data/version-control-data.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VersionControlAddComponent } from '../version-control-add/version-control-add.component';
import { VersionControlEditComponent } from '../version-control-edit/version-control-edit.component';
import { MatTabsModule } from '@angular/material/tabs';
import { VersionControlDetailTabComponent } from '../version-control-detail-tab/version-control-detail-tab.component';
import { VersionResponse } from 'admin-drivers/src/app/api/models/responses/VersionResponse';
import { Version } from 'admin-drivers/src/app/common/models/Version';
import { apiVersionControlRoutes as apiRoutes } from 'admin-drivers/src/app/api/routes/api-version-control-routes';
import { VersionControlService } from 'admin-drivers/src/app/api/services/version-control.service';

@Component({
  selector: 't-llevo-version-control-list',
  standalone: true,
  imports: [
    CommonModule,
    CardTableComponent,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatMenuModule,
    LoaderIndicatorDirective,
    SearchComponent,
    MatDialogModule,
    MatTooltipModule,
    MatTabsModule,
    VersionControlDetailTabComponent,
  ],
  providers: [
    ConfirmDialogService,
    { provide: MatPaginatorIntl, useValue: CustomPaginator() },
  ],
  templateUrl: './version-control-list.component.html',
  styles: `
    .mat-column-actions {
      display: flex;
      justify-content: center;
    }
  `,
})
export class VersionControlListComponent
  extends BaseListItems<Version, VersionResponse[]>
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @Input() searchTerm = '';
  displayedColumns: string[] = [
    'versionName',
    'versionCode',
    'required',
    'updatedAt',
    'actions',
  ];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  showDrawer = false;
  destroyRef = inject(DestroyRef);
  config;

  paginatedItems: MatTableDataSource<Version> = new MatTableDataSource();

  constructor(
    public dialog: MatDialog,
    private versionSrv: VersionControlService,
    private versionDataSrv: VersionControlDataService,
  ) {
    super(versionSrv, apiRoutes.routeVersion);
    this.config = this.versionDataSrv.getAppConfig();
  }
  ngOnInit(): void {
    this.loadItems();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchTerm']) {
      this.onSearch(changes['searchTerm'].currentValue);
    }
  }
  ngAfterViewInit(): void {
    this.dataSource.connect().subscribe((data) => {
      this.paginatedItems.data = data
        .slice(0, data.length)
        .filter((version) => version.packageName === this.config.packageName);
      this.paginatedItems.paginator = this.paginator;
    });
  }
  ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.complete();
  }

  openAddItemDialog() {
    this.dialog
      .open(VersionControlAddComponent, {
        width: '40%',
        maxHeight: '90%',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((data: boolean) => {
        if (data) {
          this.loadItems();
        }
      });
  }

  openEditDriverDialog(item: Version) {
    this.dialog
      .open(VersionControlEditComponent, {
        width: '40%',
        maxHeight: '90%',
        disableClose: true,
        data: item,
      })
      .afterClosed()
      .subscribe((data: boolean) => {
        if (data) {
          this.loadItems();
        }
      });
  }

  openDeleteDialog(item: Version) {
    const config = {
      title: `¿Desea eliminar el elemento?`,
      message: 'El elemento se eliminará de forma permanente',
      cancelText: 'CANCELAR',
      confirmText: 'SI, DESEO ELIMINAR',
    };
    this.confirmDialogSrv.open(config).subscribe((dataDialog) => {
      if (dataDialog) {
        this.versionSrv.deleteVersionById(item.id).subscribe({
          next: () => {
            this.notifySrv.dispatch('Versión eliminada', 'success');
            this.loadItems();
          },
        });
      }
    });
  }

  _getFiltersParams(): FilterParams {
    const params: FilterParams = {
      page: this.paginator ? this.paginator.pageIndex + 1 : 1,
      page_size: this.paginator ? this.paginator.pageSize : 10,
    };
    if (this.searchParam) {
      params['search'] = this.searchParam;
    }

    return params;
  }

  override onSearch(ev: string): void {
    if (this.searchParam !== ev || this.searchParam === '') {
      this.searchParam = ev;
      this.paginatedItems.filter = this.searchParam;
    }
  }

  detail(item: Version): void {
    this.showDrawer = !this.showDrawer;
    this.selectItem(item.id);
  }

  openedDrawerChange(open: boolean) {
    this.showDrawer = open;
  }

  private selectItem(id: number | string) {
    this.versionSrv.getItem('', id, false);
  }
}
